import type { SupabaseClient } from "@supabase/supabase-js";
import { useCallback, useEffect, useState } from "react";
import {
  discardTerminalConflict,
  loadOfflineCenter,
  retryOutboxOperation,
  type OfflineCenterState,
} from "../sync/operationRuntime";
import type { OfflineOperation, OutboxScope, OutboxSummary } from "../sync/types";

interface OfflineCenterPanelProps {
  client: SupabaseClient;
  farmId: string;
  actorId: string;
  onSyncSummary: (summary: OutboxSummary) => void;
}

function statusLabel(status: OfflineOperation["status"]): string {
  switch (status) {
    case "pending":
      return "در انتظار";
    case "syncing":
      return "در حال ارسال";
    case "retryable_error":
      return "نیازمند ارسال مجدد";
    case "terminal_conflict":
      return "تعارض";
    case "synced":
      return "ارسال شده";
    case "discarded":
      return "کنار گذاشته شده";
  }
}

function operationEntity(operation: OfflineOperation): string | undefined {
  if (operation.entityId) return operation.entityId;

  const tankCode = operation.payload.tankCode;
  if (typeof tankCode === "string" && tankCode.trim()) return tankCode;

  const tankId = operation.payload.tankId;
  if (typeof tankId === "string" && tankId.trim()) return tankId;

  return undefined;
}

function operationTime(operation: OfflineOperation): string {
  const value = new Date(operation.performedAt || operation.createdAt);
  if (Number.isNaN(value.getTime())) return operation.performedAt || operation.createdAt;

  return value.toLocaleString("fa-IR", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function errorMessage(error: unknown): string {
  const code = error instanceof Error ? error.message : "";

  if (code === "SYNC_MANUAL_RETRY_OFFLINE") {
    return "برای تلاش مجدد دستی، اتصال اینترنت لازم است.";
  }
  if (code === "SYNC_OPERATION_SCOPE_MISMATCH") {
    return "این عملیات متعلق به سالن یا کاربر فعال نیست.";
  }
  if (code === "SYNC_OPERATION_NOT_MANUALLY_RETRYABLE") {
    return "این عملیات در وضعیت قابل ارسال مجدد نیست.";
  }
  if (code === "SYNC_OPERATION_NOT_TERMINAL_CONFLICT") {
    return "این عملیات دیگر در وضعیت تعارض نیست.";
  }

  return "به‌روزرسانی مرکز آفلاین انجام نشد.";
}

export function OfflineCenterPanel({
  client,
  farmId,
  actorId,
  onSyncSummary,
}: OfflineCenterPanelProps) {
  const [operations, setOperations] = useState<OfflineOperation[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyOperationId, setBusyOperationId] = useState<string>();
  const [error, setError] = useState<string>();

  const scope: OutboxScope = { farmId, actorId };

  const applyState = useCallback(
    (state: OfflineCenterState) => {
      setOperations(state.operations);
      onSyncSummary(state.summary);
    },
    [onSyncSummary],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(undefined);

    try {
      applyState(await loadOfflineCenter({ farmId, actorId }));
    } catch (refreshError) {
      setError(errorMessage(refreshError));
    } finally {
      setLoading(false);
    }
  }, [actorId, applyState, farmId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function retry(operationId: string) {
    setBusyOperationId(operationId);
    setError(undefined);

    try {
      applyState(await retryOutboxOperation(client, operationId, scope));
    } catch (retryError) {
      setError(errorMessage(retryError));
    } finally {
      setBusyOperationId(undefined);
    }
  }

  async function discard(operationId: string) {
    const confirmed = window.confirm(
      "این کار فقط ثبت محلی این تعارض را کنار می‌گذارد و هیچ داده‌ای را از سرور حذف یا Rollback نمی‌کند. ادامه می‌دهید؟",
    );
    if (!confirmed) return;

    setBusyOperationId(operationId);
    setError(undefined);

    try {
      applyState(await discardTerminalConflict(operationId, scope));
    } catch (discardError) {
      setError(errorMessage(discardError));
    } finally {
      setBusyOperationId(undefined);
    }
  }

  const groups = [
    { status: "pending", label: "در انتظار" },
    { status: "syncing", label: "در حال ارسال" },
    { status: "retryable_error", label: "نیازمند ارسال مجدد" },
    { status: "terminal_conflict", label: "تعارض" },
  ] as const;

  return (
    <section className="section-card" aria-labelledby="offline-operations-title">
      <div className="section-title-row">
        <div>
          <p className="section-kicker">Outbox محلی</p>
          <h2 id="offline-operations-title">عملیات در انتظار رسیدگی</h2>
        </div>
        <button
          type="button"
          className="text-button"
          onClick={() => void refresh()}
          disabled={loading}
        >
          بازخوانی
        </button>
      </div>

      <p className="section-note">
        عملیات ارسال‌شده و کنارگذاشته‌شده از این فهرست عملیاتی حذف می‌شوند.
      </p>

      {error ? <p className="settings-warning">{error}</p> : null}
      {loading ? <p className="empty-state">در حال خواندن صف محلی…</p> : null}

      {!loading && operations.length === 0 ? (
        <p className="empty-state">عملیات باز یا تعارض محلی وجود ندارد.</p>
      ) : null}

      {groups.map((group) => {
        const items = operations.filter((operation) => operation.status === group.status);
        if (items.length === 0) return null;

        return (
          <section key={group.status} aria-label={group.label}>
            <div className="section-title-row">
              <h3>{group.label}</h3>
              <span className="count-pill">{items.length}</span>
            </div>
            <div className="work-list">
              {items.map((operation) => {
                const entity = operationEntity(operation);
                const busy = busyOperationId === operation.operationId;

                return (
                  <article className={`work-item ${operation.status}`} key={operation.operationId}>
                    <span className="work-main">
                      <strong>{operation.operationType}</strong>
                      <span>
                        {statusLabel(operation.status)}
                        {entity ? ` · تانک/Entity: ${entity}` : ""}
                        {` · ${operationTime(operation)}`}
                      </span>
                      <span>
                        تلاش‌های ناموفق: {operation.retryCount}
                        {operation.lastErrorCode ? ` · خطا: ${operation.lastErrorCode}` : ""}
                      </span>

                      {operation.status === "terminal_conflict" ? (
                        <details>
                          <summary>جزئیات تعارض</summary>
                          <pre dir="ltr">
                            {JSON.stringify(operation.conflictDetails ?? {}, null, 2)}
                          </pre>
                        </details>
                      ) : null}

                      <span style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                        {operation.status === "retryable_error" ? (
                          <button
                            type="button"
                            className="text-button"
                            disabled={busy}
                            onClick={() => void retry(operation.operationId)}
                          >
                            {busy ? "در حال تلاش…" : "تلاش مجدد الان"}
                          </button>
                        ) : null}

                        {operation.status === "terminal_conflict" ? (
                          <button
                            type="button"
                            className="text-button"
                            disabled={busy}
                            onClick={() => void discard(operation.operationId)}
                          >
                            {busy ? "در حال ثبت…" : "کنار گذاشتن ثبت محلی"}
                          </button>
                        ) : null}
                      </span>
                    </span>
                  </article>
                );
              })}
            </div>
          </section>
        );
      })}
    </section>
  );
}
